/**
 * Shared health/metric semantics for analytics surfaces.
 *
 * Single source of truth for the red/amber/green tiering used by Fleet
 * Analytics (uptime/coverage bars) and Device Analytics (uptime KPI), plus the
 * per-metric chart colors. Replaces the `uptimeColor()` helper that was
 * duplicated verbatim in analytics.tsx and device-uptime.tsx, and the
 * divergent hardcoded greens/ambers/blues across both pages.
 */
import { colors } from './tokens';

export type HealthTier = 'healthy' | 'degraded' | 'critical';

export interface HealthTierInfo {
  tier: HealthTier;
  /** Token color for the tier. */
  color: string;
  /** Human-readable label — the non-color channel for a11y (WCAG 1.4.1). */
  label: string;
  /** Compact glyph distinguishable without color (fill differs per tier). */
  glyph: string;
}

/**
 * Maps a 0–100 percentage to a health tier.
 * Thresholds: ≥90 healthy · 70–90 degraded · <70 critical.
 */
export function healthTier(pct: number): HealthTierInfo {
  if (pct >= 90) return { tier: 'healthy', color: colors.success.main, label: 'Healthy', glyph: '●' };
  if (pct >= 70) return { tier: 'degraded', color: colors.warning.main, label: 'Degraded', glyph: '◐' };
  return { tier: 'critical', color: colors.error.main, label: 'Critical', glyph: '○' };
}

/** Tier color only — drop-in replacement for the old `uptimeColor()`. */
export function healthColor(pct: number): string {
  return healthTier(pct).color;
}

/** Threshold caption for legends placed near uptime/coverage charts. */
export const HEALTH_THRESHOLD_LEGEND = '≥90% healthy · 70–90% degraded · <70% critical';

/**
 * Canonical per-metric chart colors so CPU and Memory read identically on the
 * fleet page and the device page (memory was orange on fleet, blue on device).
 */
export const metricColors = {
  cpu: colors.primary.main, // #FF6720 orange
  memory: colors.info.main, // #2F6BFF blue
} as const;
